import { useState, useEffect } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Label } from '@/components/ui/label';
import { api } from '@/lib/api';
import {
  Loader2,
  ThumbsUp,
  ThumbsDown,
  CheckCircle2,
  XCircle,
  Clock,
  MessageSquare,
  Mail,
} from 'lucide-react';

const EMAIL_TEMPLATES = {
  refundApproved: {
    label: 'Reembolso Aprovado',
    subject: 'Seu reembolso foi aprovado ✅',
    message: `Olá!\n\nSua solicitação de reembolso foi analisada e aprovada. 1 crédito foi devolvido à sua conta e já está disponível para uso.\n\nAgradecemos o seu feedback — ele nos ajuda a melhorar continuamente a qualidade das nossas gerações.\n\nAtt,\nEquipe Cataloga`,
  },
  refundRejected: {
    label: 'Reembolso Recusado',
    subject: 'Atualização sobre sua solicitação de reembolso',
    message: `Olá!\n\nAnalisamos cuidadosamente as imagens geradas e entendemos que nossa IA pode não ter atingido exatamente o resultado esperado neste caso.\n\nApós revisão detalhada, não foi possível aprovar o reembolso desta geração, pois as imagens foram entregues de acordo com os parâmetros informados.\n\nEstamos sempre melhorando nossa tecnologia e seu feedback é muito valioso para nós. Você pode tentar novamente com diferentes configurações — ficamos à disposição para ajudar.\n\nAtt,\nEquipe Cataloga`,
  },
  custom: {
    label: 'Mensagem Personalizada',
    subject: '',
    message: '',
  },
};

interface FeedbackDetailsModalProps {
  feedbackId: string;
  open: boolean;
  onClose: () => void;
  onRefundProcessed: () => void;
}

interface FeedbackDetails {
  feedback: {
    id: string;
    rating: 'satisfied' | 'unsatisfied';
    comment: string | null;
    requestRefund: string;
    status: 'pending' | 'reviewed' | 'refunded' | 'rejected';
    createdAt: string;
    adminNotes: string | null;
  };
  generation: {
    id: string;
    productName: string;
    originalImageUrl: string;
    style: string;
    status: string;
  };
  user: {
    id: string;
    email: string;
  };
  images: Array<{
    id: string;
    imageUrl: string;
  }>;
}

export function FeedbackDetailsModal({
  feedbackId,
  open,
  onClose,
  onRefundProcessed,
}: FeedbackDetailsModalProps) {
  const [details, setDetails] = useState<FeedbackDetails | null>(null);
  const [loading, setLoading] = useState(true);
  const [processing, setProcessing] = useState(false);
  const [adminNotes, setAdminNotes] = useState('');
  const [emailModalOpen, setEmailModalOpen] = useState(false);
  const [emailTemplate, setEmailTemplate] = useState<keyof typeof EMAIL_TEMPLATES>('refundApproved');
  const [emailSubject, setEmailSubject] = useState('');
  const [emailMessage, setEmailMessage] = useState('');
  const [sendingEmail, setSendingEmail] = useState(false);

  useEffect(() => {
    if (open) {
      loadDetails();
    }
  }, [open, feedbackId]);

  const loadDetails = async () => {
    try {
      const data = await api.get<FeedbackDetails>(`/api/admin/feedbacks/${feedbackId}`);
      setDetails(data);
      setAdminNotes(data.feedback.adminNotes || '');
    } catch (error) {
      console.error('Failed to load feedback details:', error);
    } finally {
      setLoading(false);
    }
  };

  const openEmailModal = (defaultTemplate: keyof typeof EMAIL_TEMPLATES) => {
    const tpl = EMAIL_TEMPLATES[defaultTemplate];
    setEmailTemplate(defaultTemplate);
    setEmailSubject(tpl.subject);
    setEmailMessage(tpl.message);
    setEmailModalOpen(true);
  };

  const handleSendEmail = async () => {
    if (!details) return;
    setSendingEmail(true);
    try {
      const payload =
        emailTemplate === 'custom'
          ? { template: 'custom', subject: emailSubject, message: emailMessage }
          : { template: emailTemplate };

      await api.post(`/api/admin/feedbacks/${feedbackId}/send-email`, payload);
      setEmailModalOpen(false);
      alert('Email enviado com sucesso!');
    } catch (error) {
      console.error('Failed to send email:', error);
      alert('Erro ao enviar email');
    } finally {
      setSendingEmail(false);
    }
  };

  const handleProcessRefund = async (approved: boolean) => {
    if (!details) return;

    setProcessing(true);
    try {
      await api.patch(`/api/feedbacks/${feedbackId}/refund`, {
        approved,
        adminNotes: adminNotes.trim() || undefined,
      });

      onRefundProcessed();
      onClose();
    } catch (error) {
      console.error('Failed to process refund:', error);
      alert('Erro ao processar reembolso');
    } finally {
      setProcessing(false);
    }
  };

  if (loading || !details) {
    return (
      <Dialog open={open} onOpenChange={onClose}>
        <DialogContent className="max-w-4xl">
          <div className="flex items-center justify-center py-12">
            <Loader2 className="h-8 w-8 animate-spin text-blue-600" />
          </div>
        </DialogContent>
      </Dialog>
    );
  }

  const canProcessRefund = details.feedback.status === 'pending' && details.feedback.requestRefund === 'true';

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-3">
            {details.feedback.rating === 'satisfied' ? (
              <ThumbsUp className="h-6 w-6 text-green-600" />
            ) : (
              <ThumbsDown className="h-6 w-6 text-red-600" />
            )}
            Detalhes do Feedback
          </DialogTitle>
          <DialogDescription>
            {details.generation.productName} • {details.user.email}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-6">
          {/* Status */}
          <div>
            <Label>Status</Label>
            <div className="mt-2">
              <Badge
                variant={
                  details.feedback.status === 'refunded' ? 'default' :
                  details.feedback.status === 'rejected' ? 'destructive' :
                  'secondary'
                }
                className="gap-1.5"
              >
                {details.feedback.status === 'refunded' && <CheckCircle2 className="h-3.5 w-3.5" />}
                {details.feedback.status === 'rejected' && <XCircle className="h-3.5 w-3.5" />}
                {details.feedback.status === 'pending' && details.feedback.requestRefund === 'true' && <Clock className="h-3.5 w-3.5" />}
                {details.feedback.status === 'pending' && details.feedback.requestRefund === 'false' && <MessageSquare className="h-3.5 w-3.5" />}
                
                {details.feedback.status === 'refunded' && 'Reembolsado'}
                {details.feedback.status === 'rejected' && 'Rejeitado'}
                {details.feedback.status === 'pending' && details.feedback.requestRefund === 'true' && 'Aguardando Análise'}
                {details.feedback.status === 'pending' && details.feedback.requestRefund === 'false' && 'Feedback Enviado'}
              </Badge>
            </div>
          </div>

          {/* Comment */}
          {details.feedback.comment && (
            <div>
              <Label>Comentário do Usuário</Label>
              <div className="mt-2 p-4 bg-gray-50 rounded-lg border">
                <p className="text-sm text-gray-700 italic">"{details.feedback.comment}"</p>
              </div>
            </div>
          )}

          {/* Refund Request */}
          {details.feedback.requestRefund === 'true' && (
            <div>
              <Label>Solicitação de Reembolso</Label>
              <div className="mt-2 p-3 bg-yellow-50 border border-yellow-200 rounded-lg">
                <p className="text-sm text-yellow-800">
                  ⚠️ Usuário solicitou devolução de 1 crédito
                </p>
              </div>
            </div>
          )}

          {/* Original Image and Style */}
          <div className="grid md:grid-cols-2 gap-4">
            {/* Original Image */}
            <div>
              <Label>Imagem Original</Label>
              <div className="mt-2 relative aspect-square rounded-lg overflow-hidden bg-gray-100 border">
                <img
                  src={details.generation.originalImageUrl}
                  alt="Original"
                  className="w-full h-full object-contain"
                />
              </div>
            </div>

            {/* Style Used */}
            <div>
              <Label>Estilo Utilizado</Label>
              <div className="mt-2 p-4 bg-blue-50 border border-blue-200 rounded-lg">
                <p className="text-sm font-medium text-blue-900">
                  {details.generation.style.replace(/_/g, ' ')}
                </p>
                <p className="text-xs text-blue-700 mt-1">
                  Status: {details.generation.status}
                </p>
              </div>
            </div>
          </div>

          {/* Generated Images */}
          <div>
            <Label>Imagens Geradas ({details.images.length})</Label>
            <div className="mt-2 grid grid-cols-2 md:grid-cols-3 gap-3">
              {details.images.map((img) => (
                <div key={img.id} className="relative aspect-square rounded-lg overflow-hidden bg-gray-100">
                  <img
                    src={img.imageUrl}
                    alt="Generated"
                    className="w-full h-full object-cover"
                  />
                </div>
              ))}
            </div>
          </div>

          {/* Admin Notes */}
          {canProcessRefund && (
            <div>
              <Label htmlFor="adminNotes">Notas do Admin (opcional)</Label>
              <textarea
                id="adminNotes"
                value={adminNotes}
                onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) => setAdminNotes(e.target.value)}
                placeholder="Adicione notas sobre sua decisão..."
                rows={3}
                className="mt-2 flex min-h-[80px] w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 resize-none"
              />
            </div>
          )}

          {/* Existing Admin Notes */}
          {details.feedback.adminNotes && !canProcessRefund && (
            <div>
              <Label>Notas do Admin</Label>
              <div className="mt-2 p-4 bg-gray-50 rounded-lg border">
                <p className="text-sm text-gray-700">{details.feedback.adminNotes}</p>
              </div>
            </div>
          )}
        </div>

        <DialogFooter className="gap-2 flex-wrap">
          <Button
            type="button"
            variant="outline"
            onClick={onClose}
            disabled={processing}
          >
            Fechar
          </Button>
          <Button
            type="button"
            variant="outline"
            onClick={() =>
              openEmailModal(
                details.feedback.status === 'refunded'
                  ? 'refundApproved'
                  : details.feedback.status === 'rejected'
                  ? 'refundRejected'
                  : 'refundApproved'
              )
            }
            disabled={processing}
            className="gap-2"
          >
            <Mail className="h-4 w-4" />
            Enviar Email
          </Button>
          {canProcessRefund && (
            <>
              <Button
                type="button"
                variant="destructive"
                onClick={() => handleProcessRefund(false)}
                disabled={processing}
              >
                {processing && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                Rejeitar Reembolso
              </Button>
              <Button
                type="button"
                onClick={() => handleProcessRefund(true)}
                disabled={processing}
              >
                {processing && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                Aprovar Reembolso
              </Button>
            </>
          )}
        </DialogFooter>
      </DialogContent>

      {/* Email Modal */}
      <Dialog open={emailModalOpen} onOpenChange={setEmailModalOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Mail className="h-5 w-5" />
              Enviar Email para {details.user.email}
            </DialogTitle>
            <DialogDescription>
              Escolha um template ou escreva uma mensagem personalizada
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            {/* Template selector */}
            <div>
              <Label>Template</Label>
              <div className="mt-2 flex flex-wrap gap-2">
                {(Object.keys(EMAIL_TEMPLATES) as Array<keyof typeof EMAIL_TEMPLATES>).map((key) => (
                  <button
                    key={key}
                    type="button"
                    onClick={() => {
                      setEmailTemplate(key);
                      setEmailSubject(EMAIL_TEMPLATES[key].subject);
                      setEmailMessage(EMAIL_TEMPLATES[key].message);
                    }}
                    className={`px-3 py-1.5 rounded-md text-sm font-medium border transition-colors ${
                      emailTemplate === key
                        ? 'bg-slate-900 text-white border-slate-900'
                        : 'bg-white text-slate-700 border-slate-300 hover:bg-slate-50'
                    }`}
                  >
                    {EMAIL_TEMPLATES[key].label}
                  </button>
                ))}
              </div>
            </div>

            {/* Subject */}
            <div>
              <Label htmlFor="emailSubject">Assunto</Label>
              <input
                id="emailSubject"
                type="text"
                value={emailSubject}
                onChange={(e) => setEmailSubject(e.target.value)}
                className="mt-2 flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
              />
            </div>

            {/* Message */}
            <div>
              <Label htmlFor="emailMessage">Mensagem</Label>
              <textarea
                id="emailMessage"
                value={emailMessage}
                onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) => setEmailMessage(e.target.value)}
                rows={10}
                className="mt-2 flex min-h-[200px] w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 resize-none"
              />
            </div>
          </div>

          <DialogFooter className="gap-2">
            <Button
              type="button"
              variant="outline"
              onClick={() => setEmailModalOpen(false)}
              disabled={sendingEmail}
            >
              Cancelar
            </Button>
            <Button
              type="button"
              onClick={handleSendEmail}
              disabled={sendingEmail || !emailSubject || !emailMessage}
              className="gap-2"
            >
              {sendingEmail && <Loader2 className="h-4 w-4 animate-spin" />}
              <Mail className="h-4 w-4" />
              Enviar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Dialog>
  );
}
